import { Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';

import { FoamingObj, Recipe, Versions } from '@usimweb/core/models';
import { ActivatedRoute } from '@angular/router';
import { ParametersComponent } from '../../components/parameters/parameters.component';
import { CompareParameters } from '../../../../core/services/compare-parameters/compare-parameters';
import { C3Service, AnimationService, ModelService } from '@usimweb/ceetron';

import { Store } from '@ngrx/store';
import * as fromStore from '@usimweb/state/store';
import { AuthHandler } from '@usimweb/core-auth';
import { ApiService } from '@usimweb/core/services';
import { Observable, take, filter } from 'rxjs';

@Component({
    selector: 'app-compare',
    templateUrl: './compare.component.html',
    styleUrls: ['./compare.component.scss'],
    standalone: false
})
export class CompareComponent implements OnInit, OnDestroy {
  public project_uuid!: string;
  public foamings: FoamingObj[] = [];
  public versions!: Versions[];
  public modelIds: string[] = [];
  public compareResult!: any;

  public resultTypes = [
    { key: 'Density', value: 'Density' },
    { key: 'RT', value: 'RT' },
    { key: 'Pressure', value: 'Pressure' },
    { key: 'shearStress', value: 'ShearStress' },
  ];

  public resultType = this.resultTypes[2].key;
  public key = 'key';
  public value = 'value';

  public result = { data: {}, layout: {} };
  public measurements: any[] = [];

  public densityCurves: any[] = [];
  public viscoCurves: any[] = [];

  public titles: string[] = [];
  public materials: string[] = [];
  public dates: string[] = [];

  public openModels = false;
  public loadViewer = false;

  public parameters: any = [];
  public progress = true;

  public selectedOptionId: string = '';
  public selectedOption!: any;

  @ViewChildren('parameter') parameterView!: QueryList<ParametersComponent>;

  public get animationBarOpen() {
    return this.modelService.animationBarOpen;
  }

  constructor(
    private apiService: ApiService,
    private authHandler: AuthHandler,
    private c3Service: C3Service,
    private modelService: ModelService,
    private animationService: AnimationService,
    private route: ActivatedRoute,
    private store: Store<fromStore.DashboardState>
  ) {}

  ngOnInit(): void {
    this.project_uuid = this.route.snapshot.paramMap.get('id')?.toString() || '';
    this.getVersions()
      .pipe(
        filter((result) => result.length > 0),
        take(1)
      )
      .subscribe((versions) => {
        this.versions = versions;
        this.compare();
      });
  }

  ngOnDestroy(): void {
    this.modelService.clearTask();
    this.store.dispatch(new fromStore.ClearCompare());
  }

  private compare(): void {
    let index = 0;
    this.route.params.forEach((x) => {
      for (const [key, value] of Object.entries(x)) {
        if (index !== 0) {
          this.modelIds.push(value);
        }
        index = index + 1;
      }
    });

    this.getParameters();
  }

  private getVersions(): Observable<Versions[]> {
    return this.store.select(fromStore.getAllVersions);
  }

  private getParameters(): void {
    this.store.select(fromStore.getCompare).subscribe((response) => {
      this.parameters = [];
      this.foamings = [];
      this.titles = [];
      this.dates = [];
      this.compareResult = response;

      if (response.parameters) {
        response.parameters.forEach((parameter: any) => {
          this.parameters.push(parameter);
        });

        response.foamings.forEach((foaming: FoamingObj, index: number) => {
          this.foamings.push(foaming);
          this.titles.push(foaming.title);
          this.dates.push('Created at: ' + foaming.created);
          const recipe = response.recipes[index];
          this.getRecipe(this.versions[index], foaming.materialTemperature, foaming.materialName || '', recipe);
        });

        this.result = {
          data: response.results['data'],
          layout: response.results['layout'],
        };
        this.progress = false;
        setTimeout(() => {
          this.hideDuplicates(true);
        }, 100);
      }
    });
  }

  private getModels(uuids: string[]) {
    const access_token = this.authHandler.accessToken || '';
    const uuidsStr = uuids.join(',');
    const subscription = this.apiService.getFoamingProjectMultiToken(uuidsStr, access_token).subscribe((tokens) => {
      subscription.unsubscribe();

      tokens.forEach((token: string, index: number) => {
        this.modelService.openModel(token, index.toString(), this.animationService, true, 'Pressure');
        let width = screen.width * 0.4;

        if (screen.width < 1200) {
          width = screen.width * 0.9;
        }

        this.c3Service._viewer[index].resizeViewer(width, 400);

        setTimeout(() => {
          this.openModels = true;
        }, 3500);
      });
    });
  }

  private getRecipe(version: Versions, temperature: number, materialName: string, recipe: Recipe): void {
    this.materials.push(materialName + ' - ' + recipe.name + ' ' + recipe.place + ' at ' + temperature + ' °C');
    this.getRTData(version);
  }

  private getRTData(version: Versions) {
    const matdata = version?.matdata;
    if (matdata) {
      const subscription = this.apiService.getRTData(matdata).subscribe((curves) => {
        subscription.unsubscribe();

        if (curves) {
          if (curves[0]) {
            this.densityCurves.push(curves[0]);
          }
          if (curves[1]) {
            this.viscoCurves.push(curves[1]);
          }
        }
      });
    }
  }

  public hideDuplicates(toggle: boolean) {
    const parameterView = this.parameterView.find((_, i) => i === 0);
    if (toggle) {
      const newList = CompareParameters.compare(parameterView?.compareList);
      if (parameterView) {
        parameterView.compareList = newList as any;
      }
    } else {
      if (parameterView) {
        parameterView.resetTable();
      }
    }
  }

  public numberToString(value: number): string {
    return value.toString();
  }

  public tabChanged(event: any) {
    const index = event.index;

    if (index === 2 && this.loadViewer === false) {
      this.loadViewer = true;

      if (this.modelService.modelOpen) {
        this.modelService.taskPool[0].alive = true;
      }
      this.getModels(this.modelIds);
    }
  }

  public selectedResultType(value: string) {
    this.modelService.setSMapScalarMulti(value);
  }

  public showVersion(selected_uuid: string, index: number) {
    this.modelIds = this.modelIds.filter((x) => x !== this.modelIds[index]);
    this.modelIds.push(selected_uuid);
    //duplicate value if only one uuid
    this.modelIds = this.modelIds.length === 1 ? [this.modelIds[0], this.modelIds[0]] : this.modelIds;
    this.store.dispatch(new fromStore.CompareVersions(this.modelIds, 'filling'));
  }

  // Density [node]
  public setResult(result: string) {
    this.modelService.setSMapScalar(result);
    this.modelService.setSMapScalar(result, 1);
  }
}
